import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SsoCallbackPage from "@/pages/SsoCallbackPage";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import SuperAdminNetworks from "@/pages/SuperAdminNetworks";
import SuperAdminNetworkCreate from "@/pages/SuperAdminNetworkCreate";
import SuperAdminNetworkDetail from "@/pages/SuperAdminNetworkDetail";
import SuperAdminAudit from "@/pages/SuperAdminAudit";
import SuperAdminUsers from "@/pages/SuperAdminUsers";
import SuperAdminCustomers from "@/pages/SuperAdminCustomers";
import CasaMadreDashboard from "@/pages/CasaMadreDashboard";
import CasaMadreProducts from "@/pages/CasaMadreProducts";
import CasaMadreCsvImport from "@/pages/CasaMadreCsvImport";
import SuperAdminCsvBulk from "@/pages/SuperAdminCsvBulk";
import CasaMadreCRM from "@/pages/CasaMadreCRM";
import CasaMadrePoInbox from "@/pages/CasaMadrePoInbox";
import CasaMadreMedia from "@/pages/CasaMadreMedia";
import CasaMadreConfigTemplates from "@/pages/CasaMadreConfigTemplates";
import CasaMadreConfigEditor from "@/pages/CasaMadreConfigEditor";
import CasaMadreLibrerie from "@/pages/CasaMadreLibrerie";
import CasaMadreAiImport from "@/pages/CasaMadreAiImport";
import SuperAdminSchema from "@/pages/SuperAdminSchema";
import CasaMadreExternalPolicy from "@/pages/CasaMadreExternalPolicy";
import RivenditoreDashboard from "@/pages/RivenditoreDashboard";
import RivenditoreCatalog from "@/pages/RivenditoreCatalog";
import RivenditoreAcquista from "@/pages/RivenditoreAcquista";
import RivenditoreExternalProducts from "@/pages/RivenditoreExternalProducts";
import RivenditoreInherit from "@/pages/RivenditoreInherit";
import OrdersList from "@/pages/OrdersList";
import TenantSettings from "@/pages/TenantSettings";
import CmsEditor from "@/pages/CmsEditor";
import StorefrontHome from "@/pages/StorefrontHome";
import StorefrontProduct from "@/pages/StorefrontProduct";
import StorefrontStyleWrapper from "@/pages/StorefrontStyle";
import StorefrontConfigura from "@/pages/StorefrontConfigura";
import SurfaceEditor from "@/pages/SurfaceEditor";
import CheckoutPage from "@/pages/CheckoutPage";
import LegalPage from "@/pages/LegalPage";
import StorefrontSearch from "@/pages/StorefrontSearch";
import CustomerRegisterPage from "@/pages/CustomerRegisterPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import Prenotazioni from "@/pages/Prenotazioni";
import PublicTracking from "@/pages/PublicTracking";
import CasaMadreRequests from "@/pages/CasaMadreRequests";
import AccountRequests from "@/pages/AccountRequests";
import CasaMadreCatalogTransfer from "@/pages/CasaMadreCatalogTransfer";

const RoleGuard = ({ roles, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const ThemedToaster = () => {
  const { isDark } = useTheme();
  return <Toaster position="top-right" theme={isDark ? "dark" : "light"} />;
};

function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<CustomerRegisterPage />} />
            <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />

            {/* SuperAdmin */}
            <Route path="/superadmin" element={<RoleGuard roles={["superadmin"]}><SuperAdminDashboard /></RoleGuard>} />
            <Route path="/superadmin/networks" element={<RoleGuard roles={["superadmin"]}><SuperAdminNetworks /></RoleGuard>} />
            <Route path="/superadmin/networks/new" element={<RoleGuard roles={["superadmin"]}><SuperAdminNetworkCreate /></RoleGuard>} />
            <Route path="/superadmin/networks/:id" element={<RoleGuard roles={["superadmin"]}><SuperAdminNetworkDetail /></RoleGuard>} />
            <Route path="/superadmin/users" element={<RoleGuard roles={["superadmin"]}><SuperAdminUsers /></RoleGuard>} />
            <Route path="/superadmin/customers" element={<RoleGuard roles={["superadmin"]}><SuperAdminCustomers /></RoleGuard>} />
            <Route path="/superadmin/audit" element={<RoleGuard roles={["superadmin"]}><SuperAdminAudit /></RoleGuard>} />

            {/* Casa Madre */}
            <Route path="/casa-madre" element={<RoleGuard roles={["casa_madre"]}><CasaMadreDashboard /></RoleGuard>} />
            <Route path="/casa-madre/products" element={<RoleGuard roles={["casa_madre"]}><CasaMadreProducts /></RoleGuard>} />
            <Route path="/casa-madre/csv-import" element={<RoleGuard roles={["casa_madre"]}><CasaMadreCsvImport /></RoleGuard>} />
            <Route path="/superadmin/csv-bulk" element={<RoleGuard roles={["superadmin"]}><SuperAdminCsvBulk /></RoleGuard>} />
            <Route path="/casa-madre/crm" element={<RoleGuard roles={["casa_madre"]}><CasaMadreCRM /></RoleGuard>} />
            <Route path="/casa-madre/storefront-catalog" element={<RoleGuard roles={["casa_madre"]}><RivenditoreCatalog /></RoleGuard>} />
            <Route path="/casa-madre/external-brands" element={<RoleGuard roles={["casa_madre"]}><CasaMadreExternalPolicy /></RoleGuard>} />
            <Route path="/casa-madre/po-inbox" element={<RoleGuard roles={["casa_madre"]}><CasaMadrePoInbox /></RoleGuard>} />
            <Route path="/casa-madre/media" element={<RoleGuard roles={["casa_madre"]}><CasaMadreMedia /></RoleGuard>} />
            <Route path="/casa-madre/config-templates" element={<RoleGuard roles={["casa_madre", "superadmin"]}><CasaMadreConfigTemplates /></RoleGuard>} />
            <Route path="/casa-madre/configuratore" element={<RoleGuard roles={["casa_madre", "superadmin"]}><CasaMadreConfigEditor /></RoleGuard>} />
            <Route path="/casa-madre/librerie" element={<RoleGuard roles={["casa_madre", "superadmin"]}><CasaMadreLibrerie /></RoleGuard>} />
            <Route path="/casa-madre/ai-import" element={<RoleGuard roles={["casa_madre", "superadmin"]}><CasaMadreAiImport /></RoleGuard>} />
            <Route path="/superadmin/schema" element={<RoleGuard roles={["superadmin"]}><SuperAdminSchema /></RoleGuard>} />
            <Route path="/casa-madre/orders" element={<RoleGuard roles={["casa_madre"]}><OrdersList /></RoleGuard>} />
            <Route path="/casa-madre/prenotazioni" element={<RoleGuard roles={["casa_madre", "rivenditore", "superadmin"]}><Prenotazioni /></RoleGuard>} />
            <Route path="/casa-madre/richieste" element={<RoleGuard roles={["casa_madre", "superadmin"]}><CasaMadreRequests /></RoleGuard>} />
            <Route path="/account/richieste" element={<RoleGuard roles={["customer"]}><AccountRequests /></RoleGuard>} />            <Route path="/rivenditore/prenotazioni" element={<RoleGuard roles={["casa_madre", "rivenditore", "superadmin"]}><Prenotazioni /></RoleGuard>} />
            <Route path="/casa-madre/cms" element={<RoleGuard roles={["casa_madre"]}><CmsEditor /></RoleGuard>} />
            <Route path="/casa-madre/settings" element={<RoleGuard roles={["casa_madre"]}><TenantSettings /></RoleGuard>} />
            <Route path="/casa-madre/catalog-transfer" element={<RoleGuard roles={["casa_madre", "superadmin"]}><CasaMadreCatalogTransfer /></RoleGuard>} />

            {/* Rivenditore */}
            <Route path="/rivenditore" element={<RoleGuard roles={["rivenditore"]}><RivenditoreDashboard /></RoleGuard>} />
            <Route path="/rivenditore/catalog" element={<RoleGuard roles={["rivenditore"]}><RivenditoreCatalog /></RoleGuard>} />
            <Route path="/rivenditore/acquista" element={<RoleGuard roles={["rivenditore"]}><RivenditoreAcquista /></RoleGuard>} />
            <Route path="/rivenditore/external-products" element={<RoleGuard roles={["rivenditore"]}><RivenditoreExternalProducts /></RoleGuard>} />
            <Route path="/rivenditore/eredita" element={<RoleGuard roles={["rivenditore"]}><RivenditoreInherit /></RoleGuard>} />
            <Route path="/rivenditore/orders" element={<RoleGuard roles={["rivenditore"]}><OrdersList /></RoleGuard>} />
            <Route path="/rivenditore/cms" element={<RoleGuard roles={["rivenditore"]}><CmsEditor /></RoleGuard>} />
            <Route path="/rivenditore/settings" element={<RoleGuard roles={["rivenditore"]}><TenantSettings /></RoleGuard>} />

            {/* Customer */}
            <Route path="/account" element={<RoleGuard roles={["customer"]}><CustomerDashboard /></RoleGuard>} />

            {/* Public Storefront */}
            <Route path="/s/:slug" element={<StorefrontHome />} />
            <Route path="/s/:slug/style/:sku_madre" element={<StorefrontStyleWrapper />} />
            <Route path="/s/:slug/configura/:sku_madre" element={<StorefrontConfigura />} />
            <Route path="/casa-madre/surface-editor/:productId" element={<SurfaceEditor />} />
            <Route path="/s/:slug/search" element={<StorefrontSearch />} />
            <Route path="/s/:slug/product/:sku" element={<StorefrontProduct />} />
            <Route path="/s/:slug/checkout" element={<CheckoutPage />} />
            <Route path="/s/:slug/legal/:page" element={<LegalPage />} />
            <Route path="/tracking/:quoteId" element={<PublicTracking />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ThemedToaster />
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
